import { useState, useEffect, useCallback, useRef } from "react";

const useProfileData = () => {
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const API_URL = process.env.REACT_APP_API;
    const prefix = 'api/usuario-editora';
    const posfix = '-usuario-editora';
    const dataFetched = useRef(false); // Para controlar se os dados já foram buscados

    const fetchProfileData = useCallback(async () => {
        const token = localStorage.getItem('token');
        
        console.log('🔍 [useProfileData] fetchProfileData chamado:', {
            hasToken: !!token,
            alreadyFetched: dataFetched.current
        });
        
        if (token && !dataFetched.current) {
            dataFetched.current = true; // Marcar como buscado para evitar requests duplicados
            
            try {
                console.log('📤 [useProfileData] Fazendo request para buscar dados...');
                setLoading(true);
                const response = await fetch(API_URL + prefix + '/dados' + posfix, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                });
                if (!response.ok) {
                    throw new Error("Erro ao obter os dados do perfil.");
                }
                const data = await response.json();
                console.log('✅ [useProfileData] Dados do perfil recebidos:', {
                    hasData: !!data,
                    dataKeys: data ? Object.keys(data) : null
                });
                setProfileData(data);
                setError(null);
            } catch (error) {
                console.error('❌ [useProfileData] Erro ao buscar dados:', error);
                setError(error.message);
                dataFetched.current = false; // Resetar em caso de erro para permitir retry
            } finally {
                setLoading(false);
            }
        } else if (!token) {
            console.log('🚫 [useProfileData] Sem token, limpando dados...');
            // Se não há token, limpar dados
            setProfileData(null);
            setLoading(false);
            dataFetched.current = false;
        }
    }, [API_URL, prefix, posfix]);

    const updateProfile = useCallback(async (addressData) => {
        const token = localStorage.getItem('token');
        
        console.log('📝 [useProfileData] updateProfile chamado:', {
            hasToken: !!token,
            addressData
        });
        
        if (token) {
            try {
                console.log('📤 [useProfileData] Enviando request de atualização...');
                const response = await fetch(API_URL + prefix + '/dados' + posfix, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                    body: JSON.stringify(addressData)
                });
                
                if (!response.ok) {
                    // Superfície do 422 do backend (ex.: complemento obrigatório):
                    // sem isto o usuário só via "Erro ao atualizar" sem saber o motivo.
                    let corpo = null;
                    try { corpo = await response.json(); } catch { /* resposta sem JSON */ }
                    const primeiroErro = corpo?.errors
                        ? Object.values(corpo.errors).flat()[0]
                        : (corpo?.error || corpo?.message);
                    const erro = new Error(primeiroErro || "Erro ao atualizar os dados do perfil.");
                    erro.status = response.status;
                    erro.details = corpo?.errors || null;
                    throw erro;
                }

                console.log('✅ [useProfileData] Dados atualizados com sucesso');
                // Atualiza os dados locais após sucesso
                setProfileData(prev => ({
                    ...prev,
                    ...addressData
                }));
                
                return true;
            } catch (error) {
                console.error('❌ [useProfileData] Erro ao atualizar:', error);
                setError(error.message);
                throw error;
            }
        }
    }, [API_URL, prefix, posfix]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        
        if (token && !dataFetched.current) {
        fetchProfileData();
        }
    }, []); // Executar apenas uma vez na montagem

    return { profileData, loading, error, fetchProfileData, updateProfile };
};

export default useProfileData;
